<template>
  <div class="pivot-config-wrapper">
    <div class="pivot-config-main">
      <!-- 字段列表区域 -->
      <div class="pivot-field-list">
        <div class="pivot-section-title">{{ $t('字段列表') }}</div>
        <a-input
          v-model:value="searchText"
          :placeholder="$t('搜索字段')"
          allow-clear
          size="small"
          style="margin-bottom: 8px"
        />
        <div class="pivot-field-pool">
          <div
            v-for="field in availableFieldRows"
            :key="field.key"
            :class="['pivot-field-item', { 'pivot-field-group': !field.isLeaf }]"
            :style="{ paddingLeft: `${8 + field.depth * 16}px` }"
            :draggable="field.isLeaf"
            @dragstart="field.isLeaf ? onDragStart($event, field.option) : undefined"
            @dragend="field.isLeaf ? onDragEnd() : undefined"
            @dblclick="field.isLeaf ? addToValues(field.option) : undefined"
          >
            <span class="pivot-field-icon">
              <database-outlined v-if="field.isLeaf" />
              <span
                v-else
                class="pivot-field-branch-dot"
              />
            </span>
            <a-tooltip
              :title="
                field.isLeaf
                  ? field.option.label + '(' + field.option.field + ')'
                  : field.option.label
              "
            >
              <span class="pivot-field-label">
                {{ field.isLeaf ? `${field.option.label}(${field.option.field})` : field.option.label }}
              </span>
            </a-tooltip>
            <span
              v-if="field.isLeaf && isFieldInRowOrCol(field.option.field)"
              class="pivot-field-tag"
            >
              {{ getFieldLocation(field.option.field) }}
            </span>
          </div>
          <div
            v-if="availableFieldRows.length === 0"
            class="pivot-field-empty"
          >
            {{ $t('未找到匹配字段') }}
          </div>
        </div>
      </div>

      <!-- 拖放区域 -->
      <div class="pivot-drop-zones">
        <!-- 列区域 -->
        <div
          class="pivot-drop-zone"
          :class="{ 'drag-over': dragOverZone === 'columns' && !dragGroupInsert }"
          @dragover.prevent="onZoneDragOver('columns', $event)"
          @dragleave="onDragLeave"
          @drop.stop="onZoneItemsDrop('columns', $event)"
        >
          <div class="pivot-zone-header">
            <span class="pivot-zone-title">
              <swap-outlined />
              {{ $t('列') }}
            </span>
            <a-tooltip :title="$t('拖拽字段到此处作为列维度；拖入已有维度组可追加字段')">
              <info-circle-outlined class="pivot-zone-tip" />
            </a-tooltip>
          </div>
          <div class="pivot-zone-items">
            <div
              v-for="(group, gIndex) in localConfig.columns"
              :key="group.id"
              class="pivot-dim-group"
              :class="{
                'drag-over': dragOverGroupZone === 'columns' && dragOverGroupIndex === gIndex,
                'drop-group-before':
                  dragGroupInsert &&
                  dragGroupInsert.zone === 'columns' &&
                  dragGroupInsert.refIndex === gIndex &&
                  dragGroupInsert.pos === 'before',
                'drop-group-after':
                  dragGroupInsert &&
                  dragGroupInsert.zone === 'columns' &&
                  dragGroupInsert.refIndex === gIndex &&
                  dragGroupInsert.pos === 'after',
              }"
              draggable="true"
              @dragstart="onGroupDragStart($event, group, 'columns')"
              @dragend="onDragEnd"
              @dragover.prevent.stop="onGroupDragOver('columns', gIndex, $event)"
              @drop.stop="onDropOnGroup('columns', gIndex, $event)"
            >
              <div class="pivot-dim-group-header">
                <DebouncedInput
                  v-model:value="group.label"
                  size="small"
                  class="pivot-group-label-input"
                  :placeholder="defaultGroupLabel(group)"
                  :title="$t('显示名称（拖入字段后自动按字段标签下划线连接，可手动覆盖）')"
                  @change="onLabelInput(group)"
                  @click.stop
                />
                <a-select
                  v-model:value="group.linkType"
                  size="small"
                  style="width: 96px"
                  :options="linkTypeOptions"
                  :placeholder="$t('连接符')"
                  @change="onConfigChange"
                  @click.stop
                />
                <close-outlined
                  class="pivot-group-remove"
                  @click.stop="removeGroup('columns', gIndex)"
                />
              </div>
              <div class="pivot-dim-group-fields">
                <div
                  v-for="(fld, fIndex) in group.fields"
                  :key="fld.field + '_' + fIndex"
                  class="pivot-field-chip"
                  :class="{
                    'drop-before':
                      dragFieldTarget &&
                      dragFieldTarget.zone === 'columns' &&
                      dragFieldTarget.groupIndex === gIndex &&
                      dragFieldTarget.fieldIndex === fIndex &&
                      dragFieldTarget.pos === 'before',
                    'drop-after':
                      dragFieldTarget &&
                      dragFieldTarget.zone === 'columns' &&
                      dragFieldTarget.groupIndex === gIndex &&
                      dragFieldTarget.fieldIndex === fIndex &&
                      dragFieldTarget.pos === 'after',
                  }"
                  draggable="true"
                  @dragstart.stop="onFieldDragStart($event, group, 'columns', fIndex)"
                  @dragend="onDragEnd"
                  @dragover="onChipDragOver('columns', gIndex, fIndex, $event)"
                  @drop="onChipDrop('columns', gIndex, fIndex, $event)"
                >
                  <span
                    class="pivot-chip-label"
                    :title="`${fld.label || fieldLabel(fld.field)}(${fld.field})`"
                  >
                    {{ `${fld.label || fieldLabel(fld.field)}(${fld.field})` }}
                  </span>
                  <a-select
                    v-model:value="fld.processFunc"
                    size="small"
                    style="width: 160px"
                    :options="processFuncOptions"
                    :placeholder="$t('处理')"
                    :allow-clear="true"
                    @change="onConfigChange"
                    @click.stop
                  />
                  <close-outlined
                    class="pivot-chip-remove"
                    @click.stop="removeFieldFromGroup('columns', gIndex, fIndex)"
                  />
                </div>
              </div>
              <div class="pivot-sort-row">
                <span class="pivot-sort-label">{{ $t('排序') }}</span>
                <a-select
                  v-model:value="group.sortOrder"
                  size="small"
                  style="width: 84px"
                  :options="sortOrderOptions"
                  :placeholder="$t('升序')"
                  :allow-clear="true"
                  @change="onConfigChange"
                  @click.stop
                />
                <a-select
                  v-model:value="group.sortBy"
                  size="small"
                  style="width: 170px"
                  show-search
                  :filter-option="filterFieldOption"
                  :options="getSortByOptions(group)"
                  :placeholder="$t('排序依据字段')"
                  :allow-clear="true"
                  @change="onGroupSortByChange(group)"
                  @click.stop
                />
                <a-select
                  v-if="group.sortBy"
                  v-model:value="group.sortProcessFunc"
                  size="small"
                  style="width: 160px"
                  :options="processFuncOptions"
                  :placeholder="$t('排序处理')"
                  :allow-clear="true"
                  @change="onConfigChange"
                  @click.stop
                />
              </div>
            </div>
            <div
              v-if="localConfig.columns.length === 0"
              class="pivot-zone-placeholder"
            >
              {{ $t('拖拽字段到此处') }}
            </div>
          </div>
        </div>

        <!-- 行区域 -->
        <div
          class="pivot-drop-zone"
          :class="{ 'drag-over': dragOverZone === 'rows' && !dragGroupInsert }"
          @dragover.prevent="onZoneDragOver('rows', $event)"
          @dragleave="onDragLeave"
          @drop.stop="onZoneItemsDrop('rows', $event)"
        >
          <div class="pivot-zone-header">
            <span class="pivot-zone-title">
              <bars-outlined />
              {{ $t('行') }}
            </span>

            <a-tooltip :title="$t('拖拽字段到此处作为行维度；拖入已有维度组可追加字段')">
              <info-circle-outlined class="pivot-zone-tip" />
            </a-tooltip>
          </div>
          <div class="pivot-zone-items">
            <div
              v-for="(group, gIndex) in localConfig.rows"
              :key="group.id"
              class="pivot-dim-group"
              :class="{
                'drag-over': dragOverGroupZone === 'rows' && dragOverGroupIndex === gIndex,
                'drop-group-before':
                  dragGroupInsert &&
                  dragGroupInsert.zone === 'rows' &&
                  dragGroupInsert.refIndex === gIndex &&
                  dragGroupInsert.pos === 'before',
                'drop-group-after':
                  dragGroupInsert &&
                  dragGroupInsert.zone === 'rows' &&
                  dragGroupInsert.refIndex === gIndex &&
                  dragGroupInsert.pos === 'after',
              }"
              draggable="true"
              @dragstart="onGroupDragStart($event, group, 'rows')"
              @dragend="onDragEnd"
              @dragover.prevent.stop="onGroupDragOver('rows', gIndex, $event)"
              @drop.stop="onDropOnGroup('rows', gIndex, $event)"
            >
              <div class="pivot-dim-group-header">
                <DebouncedInput
                  v-model:value="group.label"
                  size="small"
                  class="pivot-group-label-input"
                  :placeholder="defaultGroupLabel(group)"
                  :title="$t('显示名称（拖入字段后自动按字段标签下划线连接，可手动覆盖）')"
                  @change="onLabelInput(group)"
                  @click.stop
                />
                <a-select
                  v-model:value="group.linkType"
                  size="small"
                  style="width: 96px"
                  :options="linkTypeOptions"
                  :placeholder="$t('连接符')"
                  @change="onConfigChange"
                  @click.stop
                />
                <close-outlined
                  class="pivot-group-remove"
                  @click.stop="removeGroup('rows', gIndex)"
                />
              </div>
              <div class="pivot-dim-group-fields">
                <div
                  v-for="(fld, fIndex) in group.fields"
                  :key="fld.field + '_' + fIndex"
                  class="pivot-field-chip"
                  :class="{
                    'drop-before':
                      dragFieldTarget &&
                      dragFieldTarget.zone === 'rows' &&
                      dragFieldTarget.groupIndex === gIndex &&
                      dragFieldTarget.fieldIndex === fIndex &&
                      dragFieldTarget.pos === 'before',
                    'drop-after':
                      dragFieldTarget &&
                      dragFieldTarget.zone === 'rows' &&
                      dragFieldTarget.groupIndex === gIndex &&
                      dragFieldTarget.fieldIndex === fIndex &&
                      dragFieldTarget.pos === 'after',
                  }"
                  draggable="true"
                  @dragstart.stop="onFieldDragStart($event, group, 'rows', fIndex)"
                  @dragend="onDragEnd"
                  @dragover="onChipDragOver('rows', gIndex, fIndex, $event)"
                  @drop="onChipDrop('rows', gIndex, fIndex, $event)"
                >
                  <span
                    class="pivot-chip-label"
                    :title="`${fld.label || fieldLabel(fld.field)}(${fld.field})`"
                  >
                    {{ `${fld.label || fieldLabel(fld.field)}(${fld.field})` }}
                  </span>
                  <a-select
                    v-model:value="fld.processFunc"
                    size="small"
                    style="width: 160px"
                    :options="processFuncOptions"
                    :placeholder="$t('处理')"
                    :allow-clear="true"
                    @change="onConfigChange"
                    @click.stop
                  />
                  <close-outlined
                    class="pivot-chip-remove"
                    @click.stop="removeFieldFromGroup('rows', gIndex, fIndex)"
                  />
                </div>
              </div>
              <div class="pivot-sort-row">
                <span class="pivot-sort-label">{{ $t('排序') }}</span>
                <a-select
                  v-model:value="group.sortOrder"
                  size="small"
                  style="width: 84px"
                  :options="sortOrderOptions"
                  :placeholder="$t('升序')"
                  :allow-clear="true"
                  @change="onConfigChange"
                  @click.stop
                />
                <a-select
                  v-model:value="group.sortBy"
                  size="small"
                  style="width: 170px"
                  show-search
                  :filter-option="filterFieldOption"
                  :options="getSortByOptions(group)"
                  :placeholder="$t('排序依据字段')"
                  :allow-clear="true"
                  @change="onGroupSortByChange(group)"
                  @click.stop
                />
                <a-select
                  v-if="group.sortBy"
                  v-model:value="group.sortProcessFunc"
                  size="small"
                  style="width: 160px"
                  :options="processFuncOptions"
                  :placeholder="$t('排序处理')"
                  :allow-clear="true"
                  @change="onConfigChange"
                  @click.stop
                />
              </div>
            </div>
            <div
              v-if="localConfig.rows.length === 0"
              class="pivot-zone-placeholder"
            >
              {{ $t('拖拽字段到此处') }}
            </div>
          </div>
        </div>

        <!-- 值区域 -->
        <div
          class="pivot-drop-zone pivot-drop-zone-values"
          :class="{ 'drag-over': dragOverZone === 'values' }"
          @dragover.prevent="onDragOver('values')"
          @dragleave="onDragLeave"
          @drop.stop="onValueDrop"
        >
          <div class="pivot-zone-header">
            <span class="pivot-zone-title">
              <calculator-outlined />
              {{ $t('值') }}
            </span>
            <div class="header-item">
              <a-button
                type="link"
                size="small"
                @click="onAddValueField"
              >
                {{ $t('添加值字段') }}
              </a-button>
              <a-tooltip :title="$t('拖拽字段到此处作为值，双击字段可快速添加，也可点击添加值字段直接进入高级配置')">
                <info-circle-outlined class="pivot-zone-tip" />
              </a-tooltip>
            </div>
          </div>
          <div class="pivot-zone-items">
            <div
              v-for="(item, index) in localConfig.values"
              :key="item.id"
              class="pivot-zone-item pivot-value-item"
              :class="{
                'drag-before': dragOverZone === 'values' && dragInsertIndex === index,
                'drag-after': dragOverZone === 'values' && dragInsertIndex === index + 1,
              }"
              draggable="true"
              @dragstart="onValueDragStart($event, item)"
              @dragend="onDragEnd"
              @dragover.prevent.stop="onValueItemDragOver(index, $event)"
            >
              <div class="value-item-header">
                <DebouncedInput
                  v-model:value="item.title"
                  size="small"
                  style="width: 100px"
                  :placeholder="$t('显示名称')"
                  @change="onValueConfigChange"
                  @click.stop
                />
                <a-button
                  type="link"
                  size="small"
                  @click="onOpenValueConfig(item)"
                >
                  高级配置
                </a-button>
                <close-outlined
                  class="pivot-zone-item-remove"
                  @click="removeValue(index)"
                />
              </div>
              <div class="value-item-content">
                <valueFieldDisplay
                  :valueField="item"
                  :field-options="flatAllFields"
                  :sub-expressions="localConfig.subExpressions"
                />
              </div>
              <div class="value-item-content">
                <a-select
                  v-model:value="item.aggregationType"
                  size="small"
                  style="width: 90px"
                  :options="aggregationOptions"
                  @change="onValueConfigChange"
                  @click.stop
                />
                <a-select
                  v-model:value="item.formatFun"
                  size="small"
                  style="width: 140px"
                  :options="valueFormatOptions"
                  :placeholder="$t('显示格式')"
                  :allow-clear="true"
                  @change="onFormatFunChange(item)"
                  @click.stop
                />
                <!-- 通用数字格式：可选预设格式串，也可自定义输入（numfmt / Excel 标准格式） -->
                <div
                  v-if="item.formatFun === NUMFMT_FORMAT_KEY"
                  class="value-item-content"
                >
                  <a-auto-complete
                    v-model:value="item.formatPattern"
                    size="small"
                    style="width: 234px"
                    :options="numfmtPatternOptions"
                    :placeholder="$t('数据格式，如 #,##0.00')"
                    @change="onPatternChange"
                    @click.stop
                  />
                </div>
                <a-tooltip
                  :title="
                    $t('开启后该值字段不展示，仅参与聚合计算（仍可被聚合计算值引用）')
                  "
                >
                  <span class="value-item-hidden">
                    <a-switch
                      v-model:checked="item.hidden"
                      size="small"
                      @change="onValueConfigChange"
                      @click.stop
                    />
                    <span class="value-item-hidden-label">{{ $t('隐藏') }}</span>
                  </span>
                </a-tooltip>
              </div>
            </div>
            <div
              v-if="localConfig.values.length === 0"
              class="pivot-zone-placeholder"
            >
              {{ $t('拖拽字段到此处') }}
            </div>
          </div>
        </div>

        <!-- 子表达式区域：可复用表达式片段，供任意值字段的表达式/过滤条件引用 -->
        <div class="pivot-drop-zone pivot-drop-zone-subexpr">
          <div class="pivot-zone-header">
            <span class="pivot-zone-title">
              <function-outlined />
              {{ $t('子表达式') }}
            </span>
            <div class="header-item">
              <a-button
                type="link"
                size="small"
                @click="onAddSubExpr"
              >
                {{ $t('添加子表达式') }}
              </a-button>
              <a-tooltip
                :title="
                  $t(
                    '子表达式是可复用的表达式片段，可在值字段表达式/过滤条件中以名称引用，后端递归内联展开',
                  )
                "
              >
                <info-circle-outlined class="pivot-zone-tip" />
              </a-tooltip>
            </div>
          </div>
          <div class="pivot-zone-items subexpr-items">
            <div
              v-for="(se, i) in localConfig.subExpressions"
              :key="se.id"
              class="pivot-subexpr-card"
            >
              <div class="subexpr-card-head">
                <span class="subexpr-card-name">{{ se.name || $t('(未命名)') }}</span>
                <span class="subexpr-card-actions">
                  <a-button
                    type="link"
                    size="small"
                    @click="onEditSubExpr(i)"
                  >
                    {{ $t('编辑') }}
                  </a-button>
                  <close-outlined
                    class="pivot-zone-item-remove"
                    @click="removeSubExpr(i)"
                  />
                </span>
              </div>
              <valueFieldDisplay
                :valueField="subExprAsValueField(se)"
                :field-options="flatAllFields"
                :sub-expressions="localConfig.subExpressions"
              />
            </div>
            <div
              v-if="!localConfig.subExpressions.length"
              class="pivot-zone-placeholder"
            >
              {{ $t('暂无子表达式，点击「添加子表达式」创建可复用的表达式片段') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 聚合计算值区域：对聚合后的值字段再次求表达式，产生新列 -->
    <div class="pivot-drop-zone pivot-drop-zone-subexpr">
      <div class="pivot-zone-header">
        <span class="pivot-zone-title">
          <calculator-outlined />
          {{ $t('聚合计算值') }}
        </span>
        <div class="header-item">
          <a-button
            type="link"
            size="small"
            @click="onAddAggComputed"
          >
            {{ $t('添加聚合计算值') }}
          </a-button>
          <a-tooltip
            :title="
              $t(
                '对聚合后的值字段再次求表达式，产生新列；可引用已配置的值字段（含隐藏字段）与其它聚合计算值（嵌套引用，最多 50 层），不可引用子表达式',
              )
            "
          >
            <info-circle-outlined class="pivot-zone-tip" />
          </a-tooltip>
        </div>
      </div>
      <div class="pivot-zone-items subexpr-items">
        <div
          v-for="(ac, i) in localConfig.aggComputedValues"
          :key="ac.id"
          class="pivot-subexpr-card"
        >
          <div class="subexpr-card-head">
            <!-- 名称：无需打开弹窗即可内联修改 -->
            <DebouncedInput
              v-model:value="ac.name"
              size="small"
              class="ac-name-input"
              :placeholder="$t('名称')"
              @change="onValueConfigChange"
              @click.stop
            />
            <span class="subexpr-card-actions">
              <a-button
                type="link"
                size="small"
                @click="onEditAggComputed(i)"
              >
                {{ $t('编辑表达式') }}
              </a-button>
              <close-outlined
                class="pivot-zone-item-remove"
                @click="removeAggComputed(i)"
              />
            </span>
          </div>
          <!-- 显示格式：无需打开弹窗即可内联配置（与值字段保持一致的交互） -->
          <div class="ac-format-row">
            <a-select
              v-model:value="ac.formatFun"
              size="small"
              style="width: 140px"
              :options="valueFormatOptions"
              :placeholder="$t('显示格式')"
              :allow-clear="true"
              @change="onFormatFunChange(ac)"
              @click.stop
            />
            <!-- 通用数字格式：可选预设格式串，也可自定义输入（numfmt / Excel 标准格式） -->
            <a-auto-complete
              v-if="ac.formatFun === NUMFMT_FORMAT_KEY"
              v-model:value="ac.formatPattern"
              size="small"
              style="width: 234px"
              :options="numfmtPatternOptions"
              :placeholder="$t('数据格式，如 #,##0.00')"
              @change="onPatternChange"
              @click.stop
            />
          </div>
          <div class="ac-format-row">
            <span class="ac-format-label">{{ $t('合计算法') }}</span>
            <a-select
              v-model:value="ac.totalAggregationMode"
              size="small"
              style="width: 170px"
              :options="aggComputedTotalModeOptions"
              @change="onValueConfigChange"
              @click.stop
            />
          </div>
          <!-- 表达式预览：value token 按已配置值字段解析显示名（含隐藏字段）；aggcomputed token 按聚合计算值解析显示名 -->
          <valueFieldDisplay
            v-if="(ac.expression || []).length"
            :valueField="aggComputedAsValueField(ac)"
            :field-options="flatAllFields"
            :sub-expressions="[]"
            :value-field-options="aggComputedValueFieldOptions"
            :agg-computed-options="aggComputedRefOptions"
          />
          <span
            v-else
            class="ac-empty-expr"
          >
            {{ $t('未设置表达式') }}
          </span>
        </div>
        <div
          v-if="!localConfig.aggComputedValues.length"
          class="pivot-zone-placeholder"
        >
          {{ $t('暂无聚合计算值，点击「添加聚合计算值」基于聚合后的值字段二次计算') }}
        </div>
      </div>
    </div>

    <!-- 选项区域 -->
    <div class="pivot-options">
      <div class="pivot-options-group">
        <span class="pivot-options-label">{{ $t('结构') }}</span>
        <a-radio-group
          v-model:value="localConfig.indicatorsAsCol"
          size="small"
          option-type="button"
          button-style="solid"
          @change="onConfigChange"
        >
          <a-radio-button :value="true">{{ $t('指标在列') }}</a-radio-button>
          <a-radio-button :value="false">{{ $t('指标在行') }}</a-radio-button>
        </a-radio-group>
        <a-radio-group
          v-model:value="localConfig.rowHierarchyType"
          size="small"
          option-type="button"
          button-style="solid"
          @change="onConfigChange"
        >
          <a-radio-button value="grid">{{ $t('扁平') }}</a-radio-button>
          <a-radio-button
            :disabled="!localConfig.rows?.length || localConfig.rows.length < 2"
            value="tree"
          >
            {{ $t('树形') }}
          </a-radio-button>
          <a-radio-button
            :disabled="!localConfig.rows?.length || localConfig.rows.length < 2"
            value="grid-tree"
          >
            {{ $t('网格树形') }}
          </a-radio-button>
        </a-radio-group>
        <label class="pivot-option-input">
          <span>{{ $t('冻结行') }}</span>
          <a-input-number
            v-model:value="localConfig.frozenRowCount"
            size="small"
            :min="0"
            :precision="0"
            style="width: 96px"
            :placeholder="$t('不冻结')"
            @change="onConfigChange"
          />
        </label>
        <label class="pivot-option-input">
          <span>{{ $t('冻结列') }}</span>
          <a-input-number
            v-model:value="localConfig.frozenColCount"
            size="small"
            :min="0"
            :precision="0"
            style="width: 96px"
            :placeholder="$t('不冻结')"
            @change="onConfigChange"
          />
        </label>
      </div>
      <div class="pivot-options-group">
        <span class="pivot-options-label">{{ $t('汇总') }}</span>
        <label class="pivot-option-switch">
          <a-switch
            v-model:checked="localConfig.columnShowSubTotals"
            size="small"
            @change="onConfigChange"
          />
          <span>{{ $t('列小计') }}</span>
        </label>
        <label class="pivot-option-switch">
          <a-switch
            v-model:checked="localConfig.columnShowGrandTotals"
            size="small"
            @change="onConfigChange"
          />
          <span>{{ $t('列合计') }}</span>
        </label>
        <label
          class="pivot-option-switch"
          v-if="localConfig.rowHierarchyType === 'grid'"
        >
          <a-switch
            v-model:checked="localConfig.rowShowSubTotals"
            size="small"
            @change="onConfigChange"
          />
          <span>{{ $t('行小计') }}</span>
        </label>
        <label
          class="pivot-option-switch"
          v-if="localConfig.rowHierarchyType === 'grid'"
        >
          <a-switch
            v-model:checked="localConfig.rowShowGrandTotals"
            size="small"
            @change="onConfigChange"
          />
          <span>{{ $t('行合计') }}</span>
        </label>
      </div>
    </div>

    <!-- 子表达式编辑弹窗 -->
    <a-modal
      v-model:open="subExprModalOpen"
      :title="editingIndex >= 0 ? $t('编辑子表达式') : $t('添加子表达式')"
      width="900px"
      :body-style="{ height: 'calc(100vh - 250px)' }"
      @ok="onSubExprOk"
      @cancel="subExprModalOpen = false"
    >
      <sub-expression-editor
        v-if="subExprModalOpen"
        v-model="editingSubExpr"
        :field-options="flatAllFields"
        :sub-expressions="localConfig.subExpressions"
      />
      <template #footer>
        <a-button
          type="primary"
          :disabled="!editingSubExpr.name || !editingSubExpr.name.trim()"
          @click="onSubExprOk"
        >
          {{ $t('确定') }}
        </a-button>
        <a-button @click="subExprModalOpen = false">
          {{ $t('取消') }}
        </a-button>
      </template>
    </a-modal>

    <!-- 聚合计算值编辑弹窗：与值字段「高级配置」一致，兼容 drawer / modal 两种弹出方式 -->
    <a-drawer
      v-if="settingMode === 'drawer'"
      v-model:open="aggComputedModalOpen"
      :title="editingAggIndex >= 0 ? $t('编辑聚合计算值') : $t('添加聚合计算值')"
      :width="aggDrawerWidth"
      :placement="aggDrawerPlacement"
      :push="false"
      :mask="false"
    >
      <agg-computed-value-editor
        v-if="aggComputedModalOpen"
        v-model="editingAggComputed"
        :value-field-options="aggComputedValueFieldOptions"
        :agg-computed-options="aggComputedRefOptions"
        :show-total-aggregation-mode="true"
      />
      <template #footer>
        <div style="display: flex; justify-content: flex-end; gap: 8px">
          <a-button
            type="primary"
            :disabled="!editingAggComputed.name || !editingAggComputed.name.trim()"
            @click="onAggComputedOk"
          >
            {{ $t('确定') }}
          </a-button>
          <a-button @click="aggComputedModalOpen = false">
            {{ $t('取消') }}
          </a-button>
        </div>
      </template>
    </a-drawer>
    <a-modal
      v-else
      v-model:open="aggComputedModalOpen"
      :title="editingAggIndex >= 0 ? $t('编辑聚合计算值') : $t('添加聚合计算值')"
      width="900px"
      :body-style="{ height: 'calc(100vh - 250px)' }"
      @ok="onAggComputedOk"
      @cancel="aggComputedModalOpen = false"
    >
      <agg-computed-value-editor
        v-if="aggComputedModalOpen"
        v-model="editingAggComputed"
        :value-field-options="aggComputedValueFieldOptions"
        :agg-computed-options="aggComputedRefOptions"
        :show-total-aggregation-mode="true"
      />
      <template #footer>
        <a-button
          type="primary"
          :disabled="!editingAggComputed.name || !editingAggComputed.name.trim()"
          @click="onAggComputedOk"
        >
          {{ $t('确定') }}
        </a-button>
        <a-button @click="aggComputedModalOpen = false">
          {{ $t('取消') }}
        </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import {
  DatabaseOutlined,
  SwapOutlined,
  BarsOutlined,
  CalculatorOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  FunctionOutlined,
} from '@ant-design/icons-vue';
import { cloneDeep, debounce } from 'lodash';
import { computed, reactive, ref, watch } from 'vue';
import {
  Column,
  PivotConfig,
  PivotFieldConfig,
  PivotFieldOption,
  PivotDimField,
  PivotValueField,
  PivotSubExpression,
  PivotAggComputedValue,
  PivotAggComputedRefOption,
  SettingMode,
  SettingDrawerPlacement,
} from '../typing';
import {
  PIVOT_FIELD_PROCESS_FUNCS,
  VALUE_FORMATS,
  NUMFMT_FORMAT_KEY,
  NUMFMT_PATTERN_PRESETS,
  DEFAULT_NUMFMT_PATTERN,
  normalizeValueFormat,
} from '../func';
import { genID } from 'liyu-pc-base/utils/function';
import { combineLabel } from '../utils';
import valueFieldDisplay from './valueFieldDisplay.vue';
import DebouncedInput from './DebouncedInput.vue';
import subExpressionEditor from './subExpressionEditor.vue';
import aggComputedValueEditor from './aggComputedValueEditor.vue';
interface Props {
  columns: Column[];
  modelValue?: PivotConfig;
  settingMode: SettingMode;
  // 抽屉模式下的宽度/方向（与值字段「高级配置」抽屉保持一致，由 tableConfig/index.vue 透传）
  settingDrawerWidth?: string | number;
  settingDrawerPlacement?: SettingDrawerPlacement;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: PivotConfig): void;
  (e: 'change', value: PivotConfig): void;
  (e: 'openValueConfig', value: PivotValueField): void;
}>();

const searchText = ref('');

// 拖拽负载（判别联合）
type DragPayload =
  | { kind: 'pool'; field: PivotFieldOption }
  | { kind: 'group'; zone: 'rows' | 'columns'; groupId: string }
  | { kind: 'field'; zone: 'rows' | 'columns'; groupId: string; fieldIndex: number }
  | { kind: 'value'; field: PivotValueField };
const dragData = ref<DragPayload | null>(null);
const dragOverZone = ref<string>('');
const dragOverGroupZone = ref<string>('');
const dragOverGroupIndex = ref<number>(-1);
const dragInsertIndex = ref<number>(-1); // 值区域拖拽插入位置索引
// 组内字段重排指示：拖拽字段经过某 chip 时，记录插入位置（前/后）
const dragFieldTarget = ref<{
  zone: string;
  groupIndex: number;
  fieldIndex: number;
  pos: 'before' | 'after';
} | null>(null);
// 组间插入指示：拖拽到组外（组间间隙）新建/插入/移动维度组时，在相邻组的上/下边框高亮
const dragGroupInsert = ref<{
  zone: string;
  refIndex: number;
  pos: 'before' | 'after';
} | null>(null);

// 维度组「显示名」是否为用户手动自定义：自定义名在字段增删/重排时不被自动覆盖
const customLabels = reactive(new Set<string>());

// 重建 custom 标记：外部/旧配置中已带 label 的组视为自定义（保留），不调用
// defaultGroupLabel 以避免 setup 阶段访问尚未初始化的依赖。
const syncCustomFlags = (config: PivotConfig) => {
  customLabels.clear();
  const dims = [...(config.rows || []), ...(config.columns || [])];
  dims.forEach(g => {
    if (g.id && g.label) customLabels.add(g.id);
  });
};

// 用户手动输入显示名：标记为自定义，后续字段变化不再自动覆盖
const onLabelInput = (group: PivotFieldConfig) => {
  if (group.id) customLabels.add(group.id);
  onConfigChange();
};

const defaultConfig: PivotConfig = {
  rows: [],
  columns: [],
  values: [],
  subExpressions: [],
  aggComputedValues: [],
  indicatorsAsCol: true,
  rowHierarchyType: 'grid',
  frozenRowCount: 0,
  frozenColCount: 0,
};

const localConfig = ref<PivotConfig>(cloneDeep(props.modelValue || defaultConfig));

// 兼容旧数据：把「单字段」维度 {field,label,processFunc} 归一为「维度组」{id,fields:[...],linkType}
const normalizeDim = (d: any): PivotFieldConfig => {
  if (d && Array.isArray(d.fields)) {
    return { ...d, id: d.id || genID() };
  }
  return {
    id: d?.id || genID(),
    fields: [
      {
        field: d?.field,
        label: d?.label,
        processFunc: d?.processFunc,
        contentMap: d?.contentMap, // 旧单字段配置可能已带转换字典，兼容保留
      },
    ],
    label: d?.label, // 旧单字段配置的显示名提升到组级（兼容旧数据）
    linkType: d?.linkType || '_',
    subTotalAggregationType: d?.subTotalAggregationType,
    sortOrder: d?.sortOrder,
    sortBy: d?.sortBy,
    sortProcessFunc: d?.sortProcessFunc,
  };
};
// 确保每个值字段都有唯一 id（兼容旧数据）；同时把旧格式 key（'0.00' 等）
// 归一为「通用数字格式 + formatPattern」
const ensureValueIds = (config: PivotConfig) => {
  config.values.forEach(v => {
    if (!v.id && v.field) {
      v.id = `${v.field}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    }
    normalizeValueFormat(v);
  });
};
// 确保每个聚合计算值都有唯一 id，并把旧格式 key 归一为「通用数字格式 + formatPattern」
const ensureAggComputedIds = (config: PivotConfig) => {
  (config.aggComputedValues || []).forEach(a => {
    if (!a.id) a.id = genID();
    if (!a.totalAggregationMode) a.totalAggregationMode = 'sum_leaf';
    normalizeValueFormat(a);
  });
};
const normalizeConfig = (config: PivotConfig) => {
  config.rows = (config.rows || []).map(normalizeDim);
  config.columns = (config.columns || []).map(normalizeDim);
  if (!Array.isArray(config.subExpressions)) config.subExpressions = [];
  if (!Array.isArray(config.aggComputedValues)) config.aggComputedValues = [];
  config.frozenRowCount = Math.max(0, Math.floor(Number(config.frozenRowCount) || 0));
  config.frozenColCount = Math.max(0, Math.floor(Number(config.frozenColCount) || 0));
  ensureValueIds(config);
  ensureAggComputedIds(config);
};
normalizeConfig(localConfig.value);
syncCustomFlags(localConfig.value);

// 自身 emit 引起的 modelValue 回灌无需再 cloneDeep+重渲染（localConfig 已是最新），
// 用 skipWatch 跳过本次，避免一次 drop 触发两次整面板重渲染导致的卡顿。
let skipWatch = false;

watch(
  () => props.modelValue,
  val => {
    if (skipWatch) {
      skipWatch = false;
      return;
    }
    if (val) {
      const cloned = cloneDeep(val);
      normalizeConfig(cloned);
      syncCustomFlags(cloned);
      localConfig.value = cloned;
    }
  },
  { deep: true },
);

// 所有可用字段列表
const allFields = computed<PivotFieldOption[]>(() => {
  const buildTree = (cols: Column[]): PivotFieldOption[] => {
    return cols
      .map(col => {
        const label = col.title || col.dataIndex || col.key || '';
        const children = col.children?.length ? buildTree(col.children) : [];
        const isLeaf = children.length === 0;
        if (isLeaf) {
          if (!col.dataIndex || col.dataIndex === 'index' || col.dataIndex === 'action') {
            return null;
          }
          return {
            field: col.dataIndex,
            label,
            // 携带源字段配置的值转换字典（contentMap），拖入维度时赋值到字段的 contentMap
            contentMap: col.contentMap,
          };
        }
        return {
          field: col.dataIndex || col.key || label,
          label,
          children,
        };
      })
      .filter(Boolean) as PivotFieldOption[];
  };
  return buildTree(props.columns || []);
});

const flatAllFields = computed<PivotFieldOption[]>(() => {
  const flatten = (fields: PivotFieldOption[]): PivotFieldOption[] => {
    const list: PivotFieldOption[] = [];
    fields.forEach(field => {
      if (field.children?.length) {
        list.push(...flatten(field.children));
      } else {
        list.push({ field: field.field, label: field.label, contentMap: field.contentMap });
      }
    });
    return list;
  };
  return flatten(allFields.value);
});

const availableFieldTree = computed<PivotFieldOption[]>(() => {
  const filterTree = (fields: PivotFieldOption[], search: string): PivotFieldOption[] => {
    return fields
      .map(field => {
        const selfMatch =
          field.label.toLowerCase().includes(search) || field.field.toLowerCase().includes(search);
        const children = field.children?.length ? filterTree(field.children, search) : [];
        if (selfMatch) {
          return {
            ...field,
            children: field.children?.length ? field.children : undefined,
          };
        }
        if (children.length) {
          return {
            ...field,
            children,
          };
        }
        if (!field.children?.length && selfMatch) {
          return { ...field };
        }
        return null;
      })
      .filter(Boolean) as PivotFieldOption[];
  };

  const search = searchText.value.trim().toLowerCase();
  if (!search) return allFields.value;
  return filterTree(allFields.value, search);
});

const availableFieldRows = computed<
  Array<{ key: string; depth: number; isLeaf: boolean; option: PivotFieldOption }>
>(() => {
  const list: Array<{ key: string; depth: number; isLeaf: boolean; option: PivotFieldOption }> = [];
  const walk = (fields: PivotFieldOption[], depth = 0) => {
    fields.forEach(field => {
      const isLeaf = !field.children?.length;
      list.push({
        key: `${depth}_${field.field}_${field.label}`,
        depth,
        isLeaf,
        option: field,
      });
      if (field.children?.length) {
        walk(field.children, depth + 1);
      }
    });
  };
  walk(availableFieldTree.value);
  return list;
});

// 检查字段是否已在任意行/列维度组中
const isFieldInRowOrCol = (field: string) => {
  return (
    localConfig.value.rows.some(g => g.fields.some(f => f.field === field)) ||
    localConfig.value.columns.some(g => g.fields.some(f => f.field === field))
  );
};

// 获取字段所在位置标签（可能同时出现在行、列）
const getFieldLocation = (field: string) => {
  const locs: string[] = [];
  if (localConfig.value.rows.some(g => g.fields.some(f => f.field === field))) locs.push('行');
  if (localConfig.value.columns.some(g => g.fields.some(f => f.field === field))) locs.push('列');
  return locs.join('·');
};

const aggregationOptions = [
  { label: '求和', value: 'SUM' },
  { label: '计数', value: 'COUNT' },
  { label: '数值计数', value: 'COUNTNUM' },
  { label: '平均值', value: 'AVG' },
  { label: '最小值', value: 'MIN' },
  { label: '最大值', value: 'MAX' },
  { label: '乘积', value: 'PRODUCT' },
  { label: '方差', value: 'VAR' },
  { label: '总体方差', value: 'VARP' },
  { label: '标准偏差', value: 'STD' },
  { label: '总体标准偏差', value: 'STDP' },
  { label: '无', value: 'NONE' },
];

// 行/列维度「数据处理函数」下拉选项（来自 func.ts 注册表，新增函数后自动出现）
const processFuncOptions = computed(() =>
  PIVOT_FIELD_PROCESS_FUNCS.map(f => ({ label: f.label, value: f.key })),
);

// 值字段「显示格式」下拉选项（来自 func.ts 注册表）
const valueFormatOptions = computed(() =>
  VALUE_FORMATS.map(f => ({ label: f.label, value: f.key })),
);

// 通用数字格式的常用格式串预设（a-auto-complete 选项，支持自定义输入）
const numfmtPatternOptions = NUMFMT_PATTERN_PRESETS.map(p => ({
  value: p.value,
  label: p.label || p.value,
}));
const aggComputedTotalModeOptions = [
  { label: '按合计值重算', value: 'recompute' },
  { label: '对子单元结果求和', value: 'sum_leaf' },
];

// 切换显示格式：选中「通用数字格式」且尚无格式串时填入缺省值
// 参数用结构化类型，值字段（PivotValueField）与聚合计算值（PivotAggComputedValue）通用
const onFormatFunChange = (item: { formatFun?: string; formatPattern?: string }) => {
  if (item.formatFun === NUMFMT_FORMAT_KEY && !item.formatPattern) {
    item.formatPattern = DEFAULT_NUMFMT_PATTERN;
  }
  onValueConfigChange();
};

// 格式串输入防抖：避免逐字符输入触发接口调用
const onPatternChange = debounce(() => onValueConfigChange(), 500);

// 维度组「连接符」下拉选项：'_'（默认）/ '-' / '()'
const linkTypeOptions = [
  { label: '_', value: '_' },
  { label: '-', value: '-' },
  { label: '( )', value: '()' },
];

// 维度「排序方向」下拉选项
const sortOrderOptions = [
  { label: '升序', value: 'asc' },
  { label: '降序', value: 'desc' },
];

// 维度组「排序依据字段」下拉选项：先列出本组各字段（按原始 field 名，Rust 侧已为组内
// 每个字段登记单字段解析列，sort_by 指向该 field 即按「组内该字段」排序），再列出其余所有
// 可用字段（含值字段、其它维度、未参与维度但存在于数据中的字段）。
// 与 Rust 侧 resolve_sort 对齐：sort_by 现可解析任意存在于数据中的字段（见 pivot.rs
// 中按 sort_by 新增 __s* 辅助列的逻辑），因此不再有「只能指向已选列 / 更早维度」的限制。
const getSortByOptions = (group: PivotFieldConfig) => {
  const own = group.fields.map(f => ({
    label: `${fieldLabel(f.field)}(${f.field})`,
    value: f.field,
  }));
  const ownSet = new Set(group.fields.map(f => f.field));
  const others = flatAllFields.value
    .filter(f => !ownSet.has(f.field))
    .map(f => ({ label: `${f.label}(${f.field})`, value: f.field }));
  return [...own, ...others];
};

// 排序依据字段下拉的搜索过滤（按 label 模糊匹配）
const filterFieldOption = (input: string, option: { label?: string }) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

// 排序依据字段变化：清空后同步清除其处理函数（处理函数依赖排序依据字段存在）
const onGroupSortByChange = (group: PivotFieldConfig) => {
  if (!group.sortBy) {
    group.sortProcessFunc = undefined;
  }
  onConfigChange();
};

// 维度组默认显示名：按字段名以下划线连接（缺省值，用户可在配置中覆盖 group.label）
const defaultGroupLabel = (group: PivotFieldConfig): string => {
  if (!group.fields.length) return '';
  const labelList = group.fields.map(f => f.label || fieldLabel(f.field));
  return combineLabel(labelList, group.linkType);
};

// 字段显示名：优先用字段列表中的 title，缺省回退到字段名
const fieldLabel = (field: string): string => {
  const f = flatAllFields.value.find(x => x.field === field);
  return f ? f.label : field;
};

// 从字段池选项构造维度字段：自动携带源字段配置的 contentMap 值转换字典
const makeDimField = (option: PivotFieldOption): PivotDimField => ({
  field: option.field,
  label: option.label,
  contentMap: option.contentMap,
});

// 从字段池选项构造新维度组（单字段）
const makeDimGroup = (option: PivotFieldOption): PivotFieldConfig => ({
  id: genID(),
  fields: [makeDimField(option)],
  linkType: '_',
});

// ===== 拖拽 =====
const onDragStart = (event: DragEvent, field: PivotFieldOption) => {
  dragData.value = { kind: 'pool', field };
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', field.field);
  }
};

const onGroupDragStart = (event: DragEvent, group: PivotFieldConfig, zone: 'rows' | 'columns') => {
  dragData.value = { kind: 'group', zone, groupId: group.id! };
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
};

const onFieldDragStart = (
  event: DragEvent,
  group: PivotFieldConfig,
  zone: 'rows' | 'columns',
  fieldIndex: number,
) => {
  dragData.value = { kind: 'field', zone, groupId: group.id!, fieldIndex };
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
};

const onDragEnd = () => clearDrag();

const clearDrag = () => {
  dragData.value = null;
  dragOverZone.value = '';
  dragOverGroupZone.value = '';
  dragOverGroupIndex.value = -1;
  dragInsertIndex.value = -1;
  dragFieldTarget.value = null;
  dragGroupInsert.value = null;
};

const onDragOver = (zone: string) => {
  dragOverZone.value = zone;
  dragFieldTarget.value = null;
};

const onDragLeave = () => {
  // 只在离开整个区域时清除
};

const onGroupDragOver = (zone: 'rows' | 'columns', index: number, event: DragEvent) => {
  const d = dragData.value;
  if (!d) return;
  event.preventDefault();
  event.stopPropagation();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';

  if (d.kind === 'group') {
    // 拖拽整组进行排序：始终显示单边框插入指示（上/下），不显示 4 边框高亮。
    // 悬停在自身组上：不显示插入指示（原地不动）。
    const groupId = localConfig.value[zone][index]?.id;
    const overSelf = d.zone === zone && d.groupId === groupId;
    let nextIns: { zone: 'rows' | 'columns'; refIndex: number; pos: 'before' | 'after' } | null =
      null;
    if (!overSelf) {
      // 指针在组的上半区→插入到该组之前（高亮上边框）；下半区→之后（高亮下边框）
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      const before = event.clientY < rect.top + rect.height / 2;
      nextIns = { zone, refIndex: index, pos: before ? 'before' : 'after' };
    }
    applyGroupInsert(nextIns);
    dragOverGroupZone.value = '';
    dragOverGroupIndex.value = -1;
    dragFieldTarget.value = null;
    dragOverZone.value = '';
    return;
  }

  // 其它情况（字段 / 池字段）：悬停整组内部 → 4 边框高亮（把字段添加进该组）
  dragOverGroupZone.value = zone;
  dragOverGroupIndex.value = index;
  dragFieldTarget.value = null; // 悬停整组（非具体字段）时清除字段级指示
  dragGroupInsert.value = null; // 悬停整组（内部）时清除组间插入指示
  dragOverZone.value = ''; // 悬停整组（内部）时清除整区高亮，避免与组内 4 边框叠加
};

const findGroupIndex = (zone: 'rows' | 'columns', groupId: string) =>
  localConfig.value[zone].findIndex(g => g.id === groupId);

// 拖到区域空白：池字段→新建维度组；组→移到该区域末尾；字段→从原组移除
const onDropZone = (zone: 'rows' | 'columns') => {
  const d = dragData.value;
  if (!d) return;
  if (d.kind === 'pool') {
    const ng = makeDimGroup(d.field);
    localConfig.value[zone].push(ng);
  } else if (d.kind === 'group') {
    const srcIdx = findGroupIndex(d.zone, d.groupId);
    if (srcIdx >= 0) {
      const [g] = localConfig.value[d.zone].splice(srcIdx, 1);
      localConfig.value[zone].push(g);
    }
  } else if (d.kind === 'field') {
    const srcIdx = findGroupIndex(d.zone, d.groupId);
    if (srcIdx >= 0) {
      localConfig.value[d.zone][srcIdx].fields.splice(d.fieldIndex, 1);
      if (localConfig.value[d.zone][srcIdx].fields.length === 0) {
        localConfig.value[d.zone].splice(srcIdx, 1);
      }
    }
  }
  clearDrag();
  onConfigChange();
};

// 拖到具体维度组：池字段→追加；字段→跨组移动；组→组内重排 / 跨区移动
const onDropOnGroup = (zone: 'rows' | 'columns', gIndex: number, event: DragEvent) => {
  const d = dragData.value;
  if (!d) return;
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const before = event.clientY < rect.top + rect.height / 2;
  const targetGroup = localConfig.value[zone][gIndex];
  if (!targetGroup) return;

  if (d.kind === 'pool') {
    targetGroup.fields.push(makeDimField(d.field));
  } else if (d.kind === 'field') {
    const srcIdx = findGroupIndex(d.zone, d.groupId);
    if (srcIdx >= 0) {
      const srcGroup = localConfig.value[d.zone][srcIdx];
      const [field] = srcGroup.fields.splice(d.fieldIndex, 1);
      if (srcGroup.fields.length === 0) {
        localConfig.value[d.zone].splice(srcIdx, 1);
      }
      targetGroup.fields.push(field);
    }
  } else if (d.kind === 'group') {
    const srcIdx = findGroupIndex(d.zone, d.groupId);
    if (srcIdx >= 0) {
      const [g] = localConfig.value[d.zone].splice(srcIdx, 1);
      let tIdx = gIndex;
      if (d.zone === zone && srcIdx < gIndex) tIdx -= 1;
      localConfig.value[zone].splice(before ? tIdx : tIdx + 1, 0, g);
    }
  }
  clearDrag();
  onConfigChange();
};

// 区域级拖拽悬浮：把探测范围提升到 .pivot-drop-zone（覆盖整片区域），
// 既设置整区高亮（dragOverZone），又按指针相对各组位置计算「组间插入」指示
// （dragGroupInsert）。关键点：第一组上方与末组下方的留白属于区域 padding/header，
// 原本不在 .pivot-zone-items 内，导致那两块区域只显示整区边框、且 drop 落到末尾；
// 现在由本函数统一探测，使「第一组上方」→ 高亮组0上边框并插入到首个位置，
// 「末组下方」→ 高亮末组下边框并插入到末尾。组内拖拽由 .pivot-dim-group 的
// .stop 拦截，仍显示 4 边框（添加字段）。
const onZoneDragOver = (zone: 'rows' | 'columns', event: DragEvent) => {
  const d = dragData.value;
  if (!d) return;
  event.preventDefault();
  onDragOver(zone); // 整区高亮
  computeGroupInsert(zone, event);
};

// 计算「组间插入」指示：第一组上方→before(0)；末组下方→after(末)；组间间隙→after(i)；
// 命中组内部由 .stop 拦截不会进入本函数，故此处无需处理组内情况。
// 仅在插入目标（zone/refIndex/pos）真正变化时才更新 dragGroupInsert，
// 避免拖拽过程中每个 dragover 都触发整面板重渲染（卡顿来源之一）。
const applyGroupInsert = (
  next: { zone: 'rows' | 'columns'; refIndex: number; pos: 'before' | 'after' } | null,
) => {
  const cur = dragGroupInsert.value;
  if (
    cur &&
    next &&
    cur.zone === next.zone &&
    cur.refIndex === next.refIndex &&
    cur.pos === next.pos
  )
    return;
  if (!cur && !next) return;
  dragGroupInsert.value = next;
};

const computeGroupInsert = (zone: 'rows' | 'columns', event: DragEvent) => {
  const d = dragData.value;
  if (!d) {
    applyGroupInsert(null);
    return;
  }
  dragOverGroupZone.value = '';
  dragOverGroupIndex.value = -1;
  dragFieldTarget.value = null;
  const container = event.currentTarget as HTMLElement;
  const groups = Array.from(container.querySelectorAll('.pivot-dim-group')) as HTMLElement[];
  if (groups.length === 0) {
    applyGroupInsert(null); // 无组（空白区）由区域层级高亮处理
    return;
  }
  const y = event.clientY;
  const firstTop = groups[0].getBoundingClientRect().top;
  if (y < firstTop) {
    applyGroupInsert({ zone, refIndex: 0, pos: 'before' });
    return;
  }
  const lastBottom = groups[groups.length - 1].getBoundingClientRect().bottom;
  if (y > lastBottom) {
    applyGroupInsert({ zone, refIndex: groups.length - 1, pos: 'after' });
    return;
  }
  for (let i = 0; i < groups.length - 1; i++) {
    const top = groups[i].getBoundingClientRect();
    const nxt = groups[i + 1].getBoundingClientRect();
    if (y >= top.bottom && y <= nxt.top) {
      // 落在组 i 与组 i+1 之间：在组 i 之后插入（高亮组 i 下边框）
      applyGroupInsert({ zone, refIndex: i, pos: 'after' });
      return;
    }
  }
  applyGroupInsert(null);
};

// 在组间间隙释放：新建（池字段）/ 插入（整组重排）/ 移出（字段独立成组）维度组到对应位置。
// 未命中组间插入目标（如空白区）时回退到区域空白逻辑。
const onZoneItemsDrop = (zone: 'rows' | 'columns', _event: DragEvent) => {
  const d = dragData.value;
  const ins = dragGroupInsert.value;
  if (!d || !ins || ins.zone !== zone) {
    onDropZone(zone);
    return;
  }
  let insIndex = ins.pos === 'before' ? ins.refIndex : ins.refIndex + 1;
  if (d.kind === 'pool') {
    const ng = makeDimGroup(d.field);
    localConfig.value[zone].splice(insIndex, 0, ng);
  } else if (d.kind === 'group') {
    const srcIdx = findGroupIndex(d.zone, d.groupId);
    if (srcIdx >= 0) {
      const [g] = localConfig.value[d.zone].splice(srcIdx, 1);
      if (d.zone === zone && srcIdx < insIndex) insIndex -= 1;
      localConfig.value[zone].splice(insIndex, 0, g);
    }
  } else if (d.kind === 'field') {
    const srcGroupIdx = findGroupIndex(d.zone, d.groupId);
    if (srcGroupIdx >= 0) {
      const srcGroup = localConfig.value[d.zone][srcGroupIdx];
      const [field] = srcGroup.fields.splice(d.fieldIndex, 1);
      if (srcGroup.fields.length === 0) {
        localConfig.value[d.zone].splice(srcGroupIdx, 1);
        if (d.zone === zone && srcGroupIdx < insIndex) insIndex -= 1;
      }
      const ng: PivotFieldConfig = { id: genID(), fields: [field], linkType: '_' };
      localConfig.value[zone].splice(insIndex, 0, ng);
    }
  }
  clearDrag();
  onConfigChange();
};

// 拖拽字段经过组内某个 chip：记录插入位置（上边框=前 / 下边框=后），用于高亮提示
const onChipDragOver = (
  zone: 'rows' | 'columns',
  gIndex: number,
  fIndex: number,
  event: DragEvent,
) => {
  const d = dragData.value;
  if (!d || d.kind !== 'field') return; // 仅字段重排时处理，其它拖拽交给组/区域处理
  event.preventDefault();
  event.stopPropagation();
  dragGroupInsert.value = null; // 悬停具体字段 chip 时清除组间插入指示
  const group = localConfig.value[zone][gIndex];
  if (!group) return;
  // 悬停在被拖拽的源字段自身上时，不显示指示线（避免原地误判）
  if (d.zone === zone && d.groupId === group.id && d.fieldIndex === fIndex) {
    dragFieldTarget.value = null;
    return;
  }
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const before = event.clientY < rect.top + rect.height / 2;
  dragFieldTarget.value = {
    zone,
    groupIndex: gIndex,
    fieldIndex: fIndex,
    pos: before ? 'before' : 'after',
  };
};

// 在组内某字段的上/下边界释放：把被拖字段插入到对应位置（支持组内重排与跨组定位）
const onChipDrop = (zone: 'rows' | 'columns', gIndex: number, fIndex: number, event: DragEvent) => {
  const d = dragData.value;
  if (!d || d.kind !== 'field') return; // 非字段拖拽交给组/区域处理
  event.preventDefault();
  event.stopPropagation();
  const targetGroup = localConfig.value[zone][gIndex];
  if (!targetGroup) {
    clearDrag();
    return;
  }
  // 直接落在源字段自身：视为无移动
  if (d.zone === zone && d.groupId === targetGroup.id && d.fieldIndex === fIndex) {
    clearDrag();
    return;
  }
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const before = event.clientY < rect.top + rect.height / 2;

  const srcGroupIdx = findGroupIndex(d.zone, d.groupId);
  if (srcGroupIdx < 0) {
    clearDrag();
    return;
  }
  const srcGroup = localConfig.value[d.zone][srcGroupIdx];
  const [field] = srcGroup.fields.splice(d.fieldIndex, 1);

  const sameGroup = d.zone === zone && srcGroupIdx === gIndex;
  let targetGroupIdx = gIndex;
  let insertAt = before ? fIndex : fIndex + 1;
  // 同组内、源在目标之前时被移除会导致下标左移一位
  if (sameGroup && d.fieldIndex < fIndex) insertAt -= 1;

  // 跨组移动且源组已空：移除源组，并按需要修正目标组下标
  if (srcGroup.fields.length === 0 && !sameGroup) {
    localConfig.value[d.zone].splice(srcGroupIdx, 1);
    if (d.zone === zone && srcGroupIdx < targetGroupIdx) targetGroupIdx -= 1;
  }
  localConfig.value[zone][targetGroupIdx].fields.splice(insertAt, 0, field);
  clearDrag();
  onConfigChange();
};

// 值区域拖放：仅支持从字段池拖入新增值
const onDropValues = () => {
  const d = dragData.value;
  if (d && d.kind === 'pool') {
    addToValues(d.field);
  }
  clearDrag();
};

// 值字段拖拽排序
const onValueDragStart = (event: DragEvent, item: PivotValueField) => {
  dragData.value = { kind: 'value', field: item };
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', item.id);
  }
};

const onValueItemDragOver = (index: number, event: DragEvent) => {
  dragOverZone.value = 'values';
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const midpoint = rect.top + rect.height / 2;
  if (event.clientY < midpoint) {
    dragInsertIndex.value = index;
  } else {
    dragInsertIndex.value = index + 1;
  }
};

const onValueDrop = () => {
  const d = dragData.value;
  if (!d) return;

  // 从字段池拖入：创建新值
  if (d.kind === 'pool') {
    const insertIndex =
      dragInsertIndex.value >= 0 ? dragInsertIndex.value : localConfig.value.values.length;
    const valueField: PivotValueField = {
      id: `${d.field.field}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      field: d.field.field,
      label: d.field.label,
      aggregationType: 'SUM',
      title: d.field.label,
      useExpression: false,
      expression: [],
      filter: [],
    };
    localConfig.value.values.splice(insertIndex, 0, valueField);
    clearDrag();
    onConfigChange();
    return;
  }

  // 值区域内重排
  if (d.kind !== 'value') return;
  const list = localConfig.value.values;
  const fromIndex = list.findIndex(f => f.id === (d as any).field.id);
  if (fromIndex === -1) {
    dragOverZone.value = '';
    dragInsertIndex.value = -1;
    return;
  }
  let toIndex = dragInsertIndex.value;
  if (toIndex < 0) toIndex = list.length;
  if (toIndex > fromIndex) toIndex -= 1;
  if (toIndex >= list.length) toIndex = list.length - 1;
  if (toIndex < 0) toIndex = 0;
  const [item] = list.splice(fromIndex, 1);
  list.splice(toIndex, 0, item);
  dragOverZone.value = '';
  dragInsertIndex.value = -1;
  onConfigChange();
};

// 双击字段快速添加到值区域（允许重复添加，不限制是否已在行/列中）
const addToValues = (field: PivotFieldOption) => {
  const valueField: PivotValueField = {
    id: `${field.field}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    field: field.field,
    label: field.label,
    aggregationType: 'SUM',
    title: field.label,
    useExpression: false,
    expression: [],
    filter: [],
  };
  localConfig.value.values.push(valueField);
  onConfigChange();
};

// 值区域直接新增一个空白值字段，并立即打开高级配置面板
const onAddValueField = () => {
  const valueField: PivotValueField = {
    id: `value_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    field: '',
    aggregationType: 'SUM',
    title: '',
    useExpression: true,
    expression: [],
    filter: [],
  };
  localConfig.value.values.push(valueField);
  onConfigChange();
  onOpenValueConfig(valueField);
};

// 移除整个维度组
const removeGroup = (zone: 'rows' | 'columns', index: number) => {
  localConfig.value[zone].splice(index, 1);
  onConfigChange();
};

// 从维度组移除单个字段；若组变空则一并移除该组
const removeFieldFromGroup = (zone: 'rows' | 'columns', gIndex: number, fIndex: number) => {
  const g = localConfig.value[zone][gIndex];
  g.fields.splice(fIndex, 1);
  if (g.fields.length === 0) {
    localConfig.value[zone].splice(gIndex, 1);
  }
  onConfigChange();
};

// 移除值字段
const removeValue = (index: number) => {
  localConfig.value.values.splice(index, 1);
  onConfigChange();
};

const onValueConfigChange = () => {
  onConfigChange();
};

// ===== 子表达式（可复用表达式片段）管理 =====
const subExprModalOpen = ref(false);
const editingIndex = ref(-1); // >=0 表示编辑已有项，-1 表示新增
const editingSubExpr = ref<PivotSubExpression>({ id: '', name: '', expression: [] });

// 把子表达式映射为一个「伪值字段」，复用 valueFieldDisplay 渲染其表达式预览
const subExprAsValueField = (se: PivotSubExpression): PivotValueField => ({
  id: se.id,
  field: '',
  aggregationType: 'NONE',
  useExpression: true,
  expression: se.expression || [],
  filter: [],
});

const onAddSubExpr = () => {
  editingIndex.value = -1;
  editingSubExpr.value = { id: genID(), name: '', expression: [] };
  subExprModalOpen.value = true;
};
const onEditSubExpr = (i: number) => {
  editingIndex.value = i;
  editingSubExpr.value = cloneDeep(localConfig.value.subExpressions[i]);
  subExprModalOpen.value = true;
};
const onSubExprOk = () => {
  const se = editingSubExpr.value;
  if (!se.name || !se.name.trim()) return;
  if (editingIndex.value >= 0) {
    localConfig.value.subExpressions[editingIndex.value] = cloneDeep(se);
  } else {
    localConfig.value.subExpressions.push(cloneDeep(se));
  }
  subExprModalOpen.value = false;
  onConfigChange();
};
const removeSubExpr = (i: number) => {
  localConfig.value.subExpressions.splice(i, 1);
  onConfigChange();
};

// ===== 聚合计算值（对聚合后的值字段再次求表达式，产生新列）管理 =====
const aggComputedModalOpen = ref(false);
// 抽屉模式的宽度/方向：与值字段「高级配置」抽屉一致；未透传时回退到与 tableHeader 相同的缺省值
const aggDrawerWidth = computed<string | number>(() => props.settingDrawerWidth ?? '700px');
const aggDrawerPlacement = computed<SettingDrawerPlacement>(
  () => props.settingDrawerPlacement ?? 'right',
);
const editingAggIndex = ref(-1); // >=0 表示编辑已有项，-1 表示新增
const editingAggComputed = ref<PivotAggComputedValue>({
  id: '',
  name: '',
  expression: [],
  totalAggregationMode: 'sum_leaf',
});

// 可引用的值字段列表（聚合后的值），含隐藏字段（隐藏字段仍参与聚合、可被引用）。
// 每项含 id 与展示 label，供编辑器左侧列与 token 名称解析使用。
const aggComputedValueFieldOptions = computed<{ id: string; label: string }[]>(() =>
  (localConfig.value.values || []).map(v => ({
    id: v.id,
    label: v.title || v.label || v.field || v.id,
  })),
);

// 可引用的聚合计算值列表（含表达式，供编辑器做嵌套引用与环形依赖校验）。
// 编辑器内部按正在编辑的 id 排除自身。
const aggComputedRefOptions = computed<PivotAggComputedRefOption[]>(() =>
  (localConfig.value.aggComputedValues || []).map(a => ({
    id: a.id,
    label: a.name || a.id,
    expression: a.expression || [],
  })),
);

// 把聚合计算值映射为一个「伪值字段」，复用 valueFieldDisplay 渲染其表达式预览
const aggComputedAsValueField = (ac: PivotAggComputedValue): PivotValueField => ({
  id: ac.id,
  field: '',
  aggregationType: 'NONE',
  useExpression: true,
  expression: ac.expression || [],
  filter: [],
});

const onAddAggComputed = () => {
  editingAggIndex.value = -1;
  editingAggComputed.value = {
    id: genID(),
    name: '',
    expression: [],
    totalAggregationMode: 'sum_leaf',
  };
  aggComputedModalOpen.value = true;
};
const onEditAggComputed = (i: number) => {
  editingAggIndex.value = i;
  editingAggComputed.value = cloneDeep(localConfig.value.aggComputedValues[i]);
  if (!editingAggComputed.value.totalAggregationMode) {
    editingAggComputed.value.totalAggregationMode = 'sum_leaf';
  }
  aggComputedModalOpen.value = true;
};
const onAggComputedOk = () => {
  const ac = editingAggComputed.value;
  if (!ac.name || !ac.name.trim()) return;
  if (editingAggIndex.value >= 0) {
    localConfig.value.aggComputedValues[editingAggIndex.value] = cloneDeep(ac);
  } else {
    localConfig.value.aggComputedValues.push(cloneDeep(ac));
  }
  aggComputedModalOpen.value = false;
  onConfigChange();
};
const removeAggComputed = (i: number) => {
  localConfig.value.aggComputedValues.splice(i, 1);
  onConfigChange();
};

const onConfigChange = () => {
  skipWatch = true; // 标记本次 emit 引起的 modelValue 回灌，watch 中应跳过
  const config = cloneDeep(localConfig.value);
  emit('update:modelValue', config);
  emit('change', config);
};
const onOpenValueConfig = (valueField: PivotValueField) => {
  emit('openValueConfig', valueField);
};

// 供父级（tableConfig/index.vue）在「高级配置」抽屉内修改值字段后，触发一次完整配置回传
// getSubExpressions：把当前（含本会话内新增/编辑的）子表达式列表暴露给 index.vue，
// 使「高级配置」里的表达式编辑器能引用已配置的子表达式（不依赖上层 props 的实时回灌）。
defineExpose({
  forceEmit: onConfigChange,
  getSubExpressions: () => localConfig.value.subExpressions,
});
</script>

<style lang="less" scoped>
.pivot-config-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
}

.pivot-config-main {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.pivot-field-list {
  width: 220px;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .pivot-section-title {
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 13px;
    color: #333;
  }

  .pivot-field-pool {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .pivot-field-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    margin-bottom: 4px;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    cursor: grab;
    font-size: 13px;
    transition: all 0.2s;
    user-select: none;

    &:hover {
      border-color: #1890ff;
      background: #f0f7ff;
    }

    &:active {
      cursor: grabbing;
    }

    .pivot-field-icon {
      color: #1890ff;
      font-size: 12px;
    }

    .pivot-field-label {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .pivot-field-tag {
      flex-shrink: 0;
      font-size: 10px;
      color: #fff;
      background: #1890ff;
      border-radius: 8px;
      padding: 1px 6px;
      line-height: 16px;
    }
  }

  .pivot-field-group {
    cursor: default;
    background: #fafafa;
    font-weight: 600;

    &:hover {
      border-color: #f0f0f0;
      background: #fafafa;
    }

    .pivot-field-icon {
      color: #bfbfbf;
    }
  }

  .pivot-field-branch-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d9d9d9;
    display: inline-block;
  }

  .pivot-field-empty {
    text-align: center;
    color: #999;
    font-size: 12px;
    padding: 20px 0;
  }
}

.pivot-drop-zones {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  overflow-y: auto;
}

.pivot-drop-zone {
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  padding: 8px;
  transition: all 0.2s;
  min-height: 80px;
  flex-shrink: 0;

  &.drag-over {
    border-color: #1890ff;
    background: #f0f7ff;
  }

  .pivot-zone-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;

    .pivot-zone-title {
      font-weight: 600;
      font-size: 13px;
      color: #333;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .pivot-zone-tip {
      color: #999;
      font-size: 12px;
      cursor: help;
    }
  }

  .pivot-zone-items {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-height: 32px;
  }

  .pivot-zone-placeholder {
    color: #bbb;
    font-size: 12px;
    padding: 4px 8px;
    width: 100%;
    text-align: center;
  }
}

.pivot-zone-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #fafafa;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  cursor: grab;
  user-select: none;
  transition: all 0.2s;
  max-width: 100%;
  box-sizing: border-box;
  position: relative;

  &:hover {
    border-color: #1890ff;
    background: #f0f7ff;
  }

  &:active {
    cursor: grabbing;
  }

  .pivot-zone-item-remove {
    color: #999;
    cursor: pointer;
    font-size: 10px;
    margin-left: 2px;
    flex-shrink: 0;

    &:hover {
      color: #ff4d4f;
    }
  }
}

.pivot-value-item {
  background: #fff7e6;
  border-color: #ffd591;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: grab;
  user-select: none;

  &:hover {
    border-color: #fa8c16;
    background: #fff7e6;
  }

  &:active {
    cursor: grabbing;
  }

  &.drag-before {
    box-shadow: 0 -2px 0 0 #1890ff;
  }

  &.drag-after {
    box-shadow: 0 2px 0 0 #1890ff;
  }

  :deep(.ant-select) {
    flex: 1 1 auto;
    min-width: 70px;
  }

  :deep(.ant-input) {
    flex: 1 1 auto;
    min-width: 70px;
  }
}

.pivot-drop-zone-values {
  /* 值区域根据内容自动扩展，不再用 flex: 1 限制高度 */
  min-height: 80px;
}

.pivot-dim-group {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  padding: 6px 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: grab;
  user-select: none;
  transition: all 0.2s;
  position: relative;

  &:hover {
    border-color: #52c41a;
    background: #f6ffed;
  }

  &.drag-over {
    border-color: #52c41a;
    box-shadow: 0 0 0 2px #52c41a inset;
  }

  // 组间插入指示线：在相邻组的上/下边框高亮（蓝），提示将在此处新建/插入维度组；
  // 与「组内 4 边框高亮（绿，添加字段到该组）」区分。
  &.drop-group-before::before,
  &.drop-group-after::after {
    content: '';
    position: absolute;
    left: -2px;
    right: -2px;
    height: 0;
    border-top: 2px solid #1890ff;
    box-shadow: 0 0 4px rgba(24, 144, 255, 0.7);
    pointer-events: none;
    z-index: 2;
  }

  &.drop-group-before::before {
    top: -4px;
  }

  &.drop-group-after::after {
    bottom: -4px;
  }

  .pivot-dim-group-header {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;

    .pivot-group-label-input {
      flex: 1 1 auto;
      min-width: 0;
    }

    .pivot-group-remove {
      color: #999;
      cursor: pointer;
      font-size: 11px;
      flex-shrink: 0;

      &:hover {
        color: #ff4d4f;
      }
    }
  }

  .pivot-dim-group-fields {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 4px;
    width: 100%;
  }

  .pivot-field-chip {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    font-size: 12px;
    cursor: grab;
    user-select: none;
    position: relative;

    // 组内字段重排指示线：上/下边框高亮，提示释放后的插入位置
    &.drop-before::before,
    &.drop-after::after {
      content: '';
      position: absolute;
      left: -1px;
      right: -1px;
      height: 0;
      border-top: 2px solid #1890ff;
      box-shadow: 0 0 4px rgba(24, 144, 255, 0.7);
      pointer-events: none;
    }

    &.drop-before::before {
      top: -3px;
    }

    &.drop-after::after {
      bottom: -3px;
    }

    &:hover {
      border-color: #52c41a;
    }

    .pivot-chip-label {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .pivot-chip-remove {
      color: #999;
      cursor: pointer;
      font-size: 10px;
      flex-shrink: 0;

      &:hover {
        color: #ff4d4f;
      }
    }

    :deep(.ant-select) {
      flex: 1 1 auto;
      min-width: 80px;
    }
  }

  .pivot-sort-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    width: 100%;
    padding-top: 6px;
    border-top: 1px dashed #d9d9d9;

    .pivot-sort-label {
      font-size: 11px;
      color: #999;
      flex-shrink: 0;
    }

    :deep(.ant-select) {
      flex: 0 1 auto;
      min-width: 0;
    }
  }
}

.pivot-options {
  flex-shrink: 0;
  padding: 10px 0 4px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  .pivot-options-group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;

    .pivot-options-label {
      font-size: 12px;
      font-weight: 600;
      color: #666;
      flex-shrink: 0;
    }

    .pivot-option-switch {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #333;
      cursor: pointer;
      user-select: none;

      span {
        white-space: nowrap;
      }
    }

    .pivot-option-input {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: #333;

      span {
        white-space: nowrap;
      }
    }
  }
}
.value-item-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
}
.value-item-content {
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
}

.pivot-drop-zone-subexpr {
  background: #fafaff;
}

.subexpr-items {
  align-items: stretch;
}

.pivot-subexpr-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #efd6ff;
  border-radius: 6px;
  min-width: 220px;
  max-width: 100%;

  .subexpr-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    .subexpr-card-name {
      font-weight: 600;
      font-size: 13px;
      color: #722ed1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    // 聚合计算值：卡片内联名称输入（无需打开弹窗）
    .ac-name-input {
      flex: 1;
      min-width: 0;
      :deep(input) {
        font-weight: 600;
        font-size: 13px;
        color: #722ed1;
      }
    }
    .subexpr-card-actions {
      display: flex;
      align-items: center;
      gap: 2px;
      flex-shrink: 0;
    }
  }

  // 聚合计算值：卡片内联显示格式配置行
  .ac-format-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  .ac-format-label {
    font-size: 12px;
    color: #8c8c8c;
    flex-shrink: 0;
  }

  // 聚合计算值：表达式为空时的占位提示
  .ac-empty-expr {
    font-size: 12px;
    color: #bfbfbf;
    font-style: italic;
  }
}
</style>
